import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  getAgreement,
  getCurrentAgreementApproval,
  getAgreementApprovals,
  deleteAgreementApproval,
} from "../../../../../api/agreementsApi";
import {
  APPROVAL_STATUS_LABELS,
  APPROVAL_STEP_STATUS_LABELS,
  AGREEMENT_STATUSES_WITH_APPROVAL_PROCESS,
  canShowDeleteApprovalAction,
  getDeleteApprovalTitle,
  isAgreementApprovalInitiator,
  pickLatestApprovalFromHistory,
  resolveAgreementApprovalId,
  resolveAgreementListStatus,
  resolveApprovalInitiatorName,
} from "../agreementsListHelpers";
import { UserContext } from "../../../../../contexts/CurrentUserContext";
import "./AgreementDetail.css";

/** Этапы по макету (если с бэка не приходят) */
const DEFAULT_STAGES = [
  { name: "Задание на проектирование", key: "design_task" },
  { name: "КП субподрядчика", key: "kp" },
  { name: "Соглашение", key: "agreement" },
  { name: "Договор с субподрядчиком", key: "contract" },
  { name: "Выдача ИРД", key: "ird" },
  { name: "Выполнение работ", key: "work" },
];

const STAGE_STATUS_LABELS = {
  completed: "Завершён",
  in_progress: "В процессе",
  not_started: "Не начат",
};

const PAYMENT_TYPE_LABELS = {
  advance: "Аванс",
  ODGE: "Оплата до госэкспертизы (ОДГЭ)",
  OPGE: "Оплата после госэкспертизы (ОПГЭ)",
};

const PAYMENT_STATUS_LABELS = {
  overdue: "Просрочен",
  expected: "Ожидается",
  paid: "Оплачен",
  paid_overdue: "Оплачен с просрочкой",
  expected_ODGE: "Ожидается ОДГЭ",
  expected_OPGE: "Ожидается ОПГЭ",
  overdue_OPGE: "Просрочен ОПГЭ",
  paid_advance: "Оплачен Аванс",
};

const getApprovalStatusClass = (status) => {
  if (status === "approved" || status === "signed")
    return "agreement-detail-approval-status--approved";
  if (status === "rejected") return "agreement-detail-approval-status--rejected";
  if (status === "pending") return "agreement-detail-approval-status--pending";
  return "";
};

const getStepStatusClass = (status) => {
  if (status === "approved" || status === "signed")
    return "agreement-detail-step--approved";
  if (status === "rejected") return "agreement-detail-step--rejected";
  if (status === "pending") return "agreement-detail-step--pending";
  return "";
};

const ApprovalStepsTable = ({ steps }) => {
  const sortedSteps = [...(steps || [])].sort(
    (a, b) => (a.step_order ?? 0) - (b.step_order ?? 0),
  );

  if (sortedSteps.length === 0) {
    return <p className="agreement-detail-approval-empty">Шаги не назначены</p>;
  }

  return (
    <div className="agreement-detail-table-wrap">
      <table className="agreement-detail-table agreement-detail-approval-table">
        <thead>
          <tr>
            <th>№</th>
            <th>Согласующий</th>
            <th>Статус</th>
            <th>Комментарий</th>
            <th>Дата решения</th>
          </tr>
        </thead>
        <tbody>
          {sortedSteps.map((step) => (
            <tr key={step.id ?? step.step_order}>
              <td>{step.step_order ?? "—"}</td>
              <td>{step.approver_name ?? `ID ${step.approver_id ?? "—"}`}</td>
              <td>
                <span
                  className={`agreement-detail-approval-status ${getStepStatusClass(step.status)}`}
                >
                  {APPROVAL_STEP_STATUS_LABELS[step.status] ??
                    step.status ??
                    "—"}
                </span>
              </td>
              <td>{step.comment?.trim() ? step.comment : "—"}</td>
              <td>{formatDateTime(step.decided_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const WORKFLOW_ONLY_STATUSES = new Set([
  "draft",
  "pending",
  "approved",
  "signed",
  "rejected",
  "on_approval",
  "active",
  "closed",
]);

const resolveFooterPaymentStatus = (agreement, payments) => {
  const status = agreement?.status;
  if (status && !WORKFLOW_ONLY_STATUSES.has(status)) {
    return status;
  }
  const firstWithStatus = (payments || []).find((p) => p?.status);
  return firstWithStatus?.status ?? "expected_ODGE";
};

const AGREEMENT_STATUSES_WITH_HISTORY = new Set([
  "rejected",
  "approved",
  "signed",
  "draft",
]);

const shouldLoadApprovalHistory = (agreementStatus, activeApproval) =>
  AGREEMENT_STATUSES_WITH_HISTORY.has(agreementStatus) || activeApproval == null;

const AgreementDetail = ({
  agreementId,
  inline = false,
  fallbackCounterpartyName = null,
  onApprovalDeleted,
}) => {
  const { userData } = useContext(UserContext);
  const [agreement, setAgreement] = useState(null);
  const [currentApproval, setCurrentApproval] = useState(null);
  const [latestApproval, setLatestApproval] = useState(null);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeletingApproval, setIsDeletingApproval] = useState(false);
  const [showDeleteApprovalConfirm, setShowDeleteApprovalConfirm] =
    useState(false);

  const loadData = useCallback(async () => {
    if (!agreementId) {
      setAgreement(null);
      setCurrentApproval(null);
      setLatestApproval(null);
      setApprovalHistory([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const agreementData = await getAgreement(agreementId);
      const needsApprovalData = AGREEMENT_STATUSES_WITH_APPROVAL_PROCESS.has(
        agreementData?.status,
      );

      let activeApproval = null;
      let historyList = [];

      if (needsApprovalData) {
        activeApproval = await getCurrentAgreementApproval(agreementId).catch(
          () => null,
        );

        if (shouldLoadApprovalHistory(agreementData?.status, activeApproval)) {
          historyList = await getAgreementApprovals(agreementId).catch(() => []);
          historyList = Array.isArray(historyList) ? historyList : [];
        }
      }

      const latestFromHistory = pickLatestApprovalFromHistory(historyList);
      const resolvedLatest =
        activeApproval?.id != null ? activeApproval : latestFromHistory;

      setAgreement(agreementData);
      setCurrentApproval(activeApproval?.id != null ? activeApproval : null);
      setLatestApproval(resolvedLatest);
      setApprovalHistory(historyList);
    } catch (err) {
      setError(err.message || "Не удалось загрузить соглашение");
      setAgreement(null);
      setCurrentApproval(null);
      setLatestApproval(null);
      setApprovalHistory([]);
    } finally {
      setLoading(false);
    }
  }, [agreementId]);

  useEffect(() => {
    let cancelled = false;
    loadData().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [loadData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleDateString("ru-RU");
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("ru-RU").format(amount);
  };

  const getPaymentStatusClass = (status) => {
    if (!status) return "";
    if (
      status === "overdue" ||
      status === "paid_overdue" ||
      status === "overdue_OPGE" ||
      status === "overdue_advance"
    )
      return "agreement-detail-status--overdue";
    if (
      status === "expected" ||
      status === "expected_ODGE" ||
      status === "expected_OPGE"
    )
      return "agreement-detail-status--expected";
    if (status === "paid" || status === "paid_advance")
      return "agreement-detail-status--paid";
    return "";
  };

  const counterpartyName =
    agreement?.counterparty_summary?.display_name ??
    agreement?.counterparty_summary?.full_name ??
    agreement?.counterparty_summary?.full_company_name ??
    agreement?.counterparty_summary?.short_company_name ??
    agreement?.counterparty_name ??
    agreement?.company_name ??
    fallbackCounterpartyName ??
    (Array.isArray(agreement?.performer_names) &&
    agreement.performer_names.length > 0
      ? agreement.performer_names.filter(Boolean).join(", ")
      : null) ??
    (agreement?.counterparty_id != null
      ? `Контрагент #${agreement.counterparty_id}`
      : agreement?.company_id != null
        ? `Компания #${agreement.company_id}`
        : "Соглашение");

  const stages =
    Array.isArray(agreement?.stages) && agreement.stages.length > 0
      ? agreement.stages
      : DEFAULT_STAGES.map((s, i) => ({
          ...s,
          status: i < 4 ? "completed" : "not_started",
        }));

  const currentStage = agreement?.current_stage ?? {};
  const payments =
    Array.isArray(agreement?.payments) && agreement.payments.length > 0
      ? agreement.payments
      : [
          {
            payment_type: "advance",
            percent: 30,
            plan_amount: (agreement?.price ?? 0) * 0.3,
            fact_amount: null,
            plan_date: null,
            fact_date: null,
            status: "overdue",
          },
          {
            payment_type: "ODGE",
            percent: 50,
            plan_amount: (agreement?.price ?? 0) * 0.5,
            fact_amount: null,
            plan_date: null,
            fact_date: null,
            status: "expected",
          },
          {
            payment_type: "OPGE",
            percent: 20,
            plan_amount: (agreement?.price ?? 0) * 0.2,
            fact_amount: null,
            plan_date: null,
            fact_date: null,
            status: null,
          },
        ];

  const blockLabels = (agreement?.block_ids?.length ? agreement.block_ids : []).slice(
    0,
    2,
  );
  const overallPaymentStatus = resolveFooterPaymentStatus(agreement, payments);

  const displayApproval = latestApproval?.id != null ? latestApproval : currentApproval;

  const pastApprovals = approvalHistory.filter(
    (item) =>
      !displayApproval || Number(item.id) !== Number(displayApproval.id),
  );

  const workflowListStatus = resolveAgreementListStatus(agreement);
  const hadApprovalProcess = ["pending", "rejected", "approved"].includes(
    workflowListStatus,
  );

  const canDeleteCurrentApproval = canShowDeleteApprovalAction(
    agreement,
    userData?.id,
    displayApproval,
  );

  const deleteApprovalTitle = getDeleteApprovalTitle(agreement);
  const initiatorName = resolveApprovalInitiatorName(displayApproval, agreement);

  const handleDeleteApproval = async () => {
    if (
      !canDeleteCurrentApproval ||
      !agreement ||
      !isAgreementApprovalInitiator(displayApproval, agreement, userData?.id)
    ) {
      return;
    }
    setIsDeletingApproval(true);
    setError(null);
    try {
      const approvalId = await resolveAgreementApprovalId(agreement);
      if (approvalId == null) {
        throw new Error("Процесс согласования не найден");
      }
      await deleteAgreementApproval(approvalId);
      setShowDeleteApprovalConfirm(false);
      onApprovalDeleted?.();
      await loadData();
    } catch (err) {
      setError(err.message || "Не удалось отменить согласование");
    } finally {
      setIsDeletingApproval(false);
    }
  };

  if (!agreementId) return null;

  const content = (
    <>
      {loading && <div className="agreement-detail-loading">Загрузка...</div>}

      {error && (
        <div className="agreement-detail-error" role="alert">
          {error}
        </div>
      )}

      {!loading && agreement && (
        <>
          <h2 className="agreement-detail-title">
            Этапы по соглашению с {counterpartyName}
          </h2>

          <section className="agreement-detail-section">
            <div className="agreement-detail-section-header">
              <h3 className="agreement-detail-section-title">СОГЛАСОВАНИЕ</h3>
              {canDeleteCurrentApproval && (
                <button
                  type="button"
                  className="agreement-detail-cancel-approval-btn"
                  onClick={() => setShowDeleteApprovalConfirm(true)}
                  disabled={isDeletingApproval}
                >
                  {deleteApprovalTitle}
                </button>
              )}
            </div>
            {displayApproval ? (
              <div className="agreement-detail-approval-card">
                <div className="agreement-detail-approval-meta">
                  <span
                    className={`agreement-detail-approval-status ${getApprovalStatusClass(displayApproval.status)}`}
                  >
                    {APPROVAL_STATUS_LABELS[displayApproval.status] ??
                      displayApproval.status ??
                      "—"}
                  </span>
                  <span className="agreement-detail-approval-date">
                    Отправлено: {formatDateTime(displayApproval.created_at)}
                  </span>
                  {initiatorName && (
                    <span className="agreement-detail-approval-initiator">
                      Инициатор: {initiatorName}
                    </span>
                  )}
                </div>
                {displayApproval.comment?.trim() && (
                  <div className="agreement-detail-approval-comment">
                    <span className="agreement-detail-approval-comment-label">
                      Комментарий при отправке:
                    </span>
                    <span>{displayApproval.comment}</span>
                  </div>
                )}
                <ApprovalStepsTable steps={displayApproval.steps} />
              </div>
            ) : (
              <p className="agreement-detail-approval-empty">
                {hadApprovalProcess
                  ? "Детали процесса согласования недоступны"
                  : "Соглашение не отправлялось на согласование"}
              </p>
            )}
          </section>

          {showDeleteApprovalConfirm && (
            <div
              className="agreement-detail-approval-confirm"
              role="alertdialog"
              aria-labelledby="agreement-detail-delete-approval-title"
            >
              <p id="agreement-detail-delete-approval-title">
                Отменить отправку на согласование? Процесс согласования будет
                удалён.
              </p>
              <div className="agreement-detail-approval-confirm-actions">
                <button
                  type="button"
                  className="agreement-detail-approval-confirm-btn agreement-detail-approval-confirm-btn--cancel"
                  onClick={() => setShowDeleteApprovalConfirm(false)}
                  disabled={isDeletingApproval}
                >
                  Нет
                </button>
                <button
                  type="button"
                  className="agreement-detail-approval-confirm-btn agreement-detail-approval-confirm-btn--confirm"
                  onClick={handleDeleteApproval}
                  disabled={isDeletingApproval}
                >
                  {isDeletingApproval ? "Удаление..." : "Отменить согласование"}
                </button>
              </div>
            </div>
          )}

          {pastApprovals.length > 0 && (
            <section className="agreement-detail-section">
              <h3 className="agreement-detail-section-title">
                ИСТОРИЯ СОГЛАСОВАНИЙ
              </h3>
              <div className="agreement-detail-approval-history">
                {[...pastApprovals]
                  .sort(
                    (a, b) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime(),
                  )
                  .map((approval) => (
                    <details
                      key={approval.id}
                      className="agreement-detail-approval-history-item"
                    >
                      <summary className="agreement-detail-approval-history-summary">
                        <span
                          className={`agreement-detail-approval-status ${getApprovalStatusClass(approval.status)}`}
                        >
                          {APPROVAL_STATUS_LABELS[approval.status] ??
                            approval.status ??
                            "—"}
                        </span>
                        <span className="agreement-detail-approval-history-date">
                          {formatDateTime(approval.created_at)}
                        </span>
                        {approval.initiator_name && (
                          <span className="agreement-detail-approval-initiator">
                            Инициатор: {approval.initiator_name}
                          </span>
                        )}
                        {approval.comment?.trim() && (
                          <span className="agreement-detail-approval-history-comment">
                            «{approval.comment}»
                          </span>
                        )}
                      </summary>
                      <div className="agreement-detail-approval-history-body">
                        <ApprovalStepsTable steps={approval.steps} />
                      </div>
                    </details>
                  ))}
              </div>
            </section>
          )}

          <div className="agreement-detail-timeline">
            {stages.map((stage, idx) => (
              <div
                key={stage.key ?? stage.id ?? idx}
                className={`agreement-detail-timeline-step agreement-detail-timeline-step--${stage.status ?? "not_started"}`}
              >
                <div className="agreement-detail-timeline-icon">
                  {stage.status === "completed" ? "✓" : idx + 1}
                </div>
                <div className="agreement-detail-timeline-label">
                  {stage.name}
                </div>
                <div className="agreement-detail-timeline-status">
                  {STAGE_STATUS_LABELS[stage.status] ?? "Не начат"}
                </div>
                {idx < stages.length - 1 && (
                  <div className="agreement-detail-timeline-connector" />
                )}
              </div>
            ))}
          </div>

          <section className="agreement-detail-section">
            <h3 className="agreement-detail-section-title">ЭТАП</h3>
            <div className="agreement-detail-stage-grid">
              <div className="agreement-detail-stage-item">
                <label>Дата начала (план)</label>
                <div>{formatDate(currentStage.plan_start)}</div>
              </div>
              <div className="agreement-detail-stage-item">
                <label>Дата окончания (план)</label>
                <div
                  className={
                    currentStage.plan_end_overdue
                      ? "agreement-detail-date-overdue"
                      : ""
                  }
                >
                  {formatDate(currentStage.plan_end)}
                  {currentStage.plan_end_overdue && " (просрочено)"}
                </div>
              </div>
              <div className="agreement-detail-stage-item">
                <label>Дата начала (факт)</label>
                <div>{formatDate(currentStage.fact_start)}</div>
              </div>
              <div className="agreement-detail-stage-item">
                <label>Дата окончания (факт)</label>
                <div>{formatDate(currentStage.fact_end)}</div>
              </div>
              <div className="agreement-detail-stage-item agreement-detail-stage-item--full">
                <label>Статус</label>
                <span
                  className={`agreement-detail-status-badge agreement-detail-status-badge--${currentStage.status ?? "in_progress"}`}
                >
                  {STAGE_STATUS_LABELS[currentStage.status] ?? "В процессе"}
                </span>
              </div>
            </div>
            {currentStage.alert_message && (
              <div className="agreement-detail-alert" role="alert">
                <span className="agreement-detail-alert-icon">ℹ</span>
                {currentStage.alert_message}
              </div>
            )}
          </section>

          <section className="agreement-detail-section">
            <h3 className="agreement-detail-section-title">СТАТУС ОПЛАТЫ</h3>
            <div className="agreement-detail-table-wrap">
              <table className="agreement-detail-table">
                <thead>
                  <tr>
                    <th>Платёж</th>
                    <th>%</th>
                    <th>Сумма (план)</th>
                    <th>Сумма (факт)</th>
                    <th>Дата (план)</th>
                    <th>Дата (факт)</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((row, i) => (
                    <tr key={row.payment_type ?? i}>
                      <td>
                        {PAYMENT_TYPE_LABELS[row.payment_type] ??
                          row.payment_type ??
                          "—"}
                      </td>
                      <td>{row.percent != null ? `${row.percent}%` : "—"}</td>
                      <td>{formatCurrency(row.plan_amount)} ₸</td>
                      <td>{formatCurrency(row.fact_amount)}</td>
                      <td>{formatDate(row.plan_date)}</td>
                      <td>{formatDate(row.fact_date)}</td>
                      <td>
                        {row.status ? (
                          <span
                            className={`agreement-detail-status ${getPaymentStatusClass(row.status)}`}
                          >
                            {PAYMENT_STATUS_LABELS[row.status] ?? row.status}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="agreement-detail-footer">
            <div className="agreement-detail-footer-left">
              <span className="agreement-detail-footer-subcontractor">
                &gt; {counterpartyName}
              </span>
              {blockLabels.length > 0 && (
                <span className="agreement-detail-footer-blocks">
                  {blockLabels.map((b, i) => (
                    <span key={i} className="agreement-detail-footer-block-tag">
                      {b}
                    </span>
                  ))}
                </span>
              )}
              <span className="agreement-detail-footer-total">
                ₸ {formatCurrency(agreement.price)}
              </span>
            </div>
            <div className="agreement-detail-footer-right">
              <span className="agreement-detail-footer-stage-badge">
                {stages[5]?.name ?? "Выполнение работ"} · В процессе
              </span>
              <span
                className={`agreement-detail-status agreement-detail-status--footer ${getPaymentStatusClass(overallPaymentStatus)}`}
              >
                {PAYMENT_STATUS_LABELS[overallPaymentStatus] ??
                  overallPaymentStatus ??
                  "Ожидается ОДГЭ"}
              </span>
              <button
                type="button"
                className="agreement-detail-action view-btn"
                title="Просмотр"
              >
                👁
              </button>
              <button
                type="button"
                className="agreement-detail-action edit-btn"
                title="Редактировать"
              >
                ✎
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className={inline ? "agreement-detail-inline" : "agreement-detail-modal"}>
      {content}
    </div>
  );
};

export default AgreementDetail;
